import { Box, Chip, CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'

import EntityListTemplate from '../../templates/EntityListTemplate'
import { Link } from 'react-router-dom'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { getGridSingleSelectOperators } from '@mui/x-data-grid'
import { getGridStringOperators } from '@mui/x-data-grid'

const AnalysisChainsPage = () => {
  const singleSelectOperators = getGridSingleSelectOperators()
  const boolFilterOperators = singleSelectOperators.filter(
    (operator) => operator.value === 'is' || operator.value === 'isNot',
  )

  const arrayFilterOperators = singleSelectOperators.filter(
    (operator) => operator.value === 'isAnyOf',
  )

  const stringOperators = getGridStringOperators().filter(
    (operator) =>
      operator.value === 'contains' ||
      operator.value === 'equals' ||
      operator.value === 'startsWith',
  )
  const [dataTypes, setDataTypes] = useState([])
  const [analyses, setAnalyses] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dataTypesResponse, analysesResponse] = await Promise.all([
          fetchWithAuth('data-types'),
          fetchWithAuth('analyses'),
        ])

        if (dataTypesResponse.status === 200) {
          const fetchedDataTypes = dataTypesResponse.data.data

          setDataTypes(fetchedDataTypes)
        }
        if (analysesResponse.status === 200) {
          setAnalyses(analysesResponse.data.data)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      filterable: true,
      filterOperators: stringOperators,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      filterable: false,
      filterOperators: stringOperators,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'is_basic',
      headerName: 'Is Basic',
      flex: 1,
      filterable: false,
      sortable: false,
      type: 'boolean',
      disableColumnMenu: true,
    },
    {
      field: 'is_auto_compatible',
      headerName: 'Is Auto Compatible',
      flex: 1,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      filterOperators: boolFilterOperators,
      valueOptions: ['true', 'false'],
    },
    {
      field: 'analysis',
      headerName: 'Analyses',
      flex: 3,
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      filterOperators: arrayFilterOperators,
      valueOptions:
        analyses?.map((analysis) => ({
          value: analysis.uuid,
          label: analysis.name,
        })) || [],

      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {params.row?.analyses?.map((analysis, index) => (
            <Chip
              key={analysis.uuid}
              label={`${index + 1}. ${analysis.name}`}
              component={Link}
              to={`/analyses/${analysis.uuid}`}
              variant="outlined"
              clickable
              sx={{
                m: 1,
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'lastAnalysisName',
      headerName: 'Final Analysis',
      flex: 3,
      sortable: true,
      filterable: false,
      valueGetter: (params) => params?.row?.lastAnalysisName || '',
      renderCell: (params) => {
        const lastAnalysis =
          params.row?.analyses?.[params.row.analyses.length - 1]
        return lastAnalysis ? (
          <Chip
            label={lastAnalysis.name}
            component={Link}
            to={`/analyses/${lastAnalysis.uuid}`}
            variant="outlined"
            clickable
            sx={{ m: 1 }}
          />
        ) : null
      },
    },
    {
      field: 'input_datatype_ids',
      headerName: 'Input Data Types',
      flex: 4,
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      filterOperators: arrayFilterOperators,
      valueOptions:
        dataTypes?.map((dt) => ({
          value: dt.uuid,
          label: dt.name,
        })) || [],
      valueGetter: (params) =>
        (params &&
          params.row?.inputDataTypes?.map((dt) => dt.name).join(', ')) ||
        '',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {params.row?.inputDataTypes?.map((dataType) => (
            <Chip
              key={dataType.uuid}
              label={dataType.name}
              component={Link}
              to={`/data-types/${dataType.uuid}`}
              clickable
              variant="outlined"
              sx={{
                m: 1,
              }}
            />
          ))}
        </Box>
      ),
    },
  ]

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <EntityListTemplate
      entityName="analysis-chains"
      title="Analysis Chains"
      columns={columns}
      defaultSort={[{ field: 'lastAnalysisName', sort: 'desc' }]}
      defaultFilters={{
        items: [],
      }}
      addButtonLink="/analysis-chains/create"
      tableProps={{
        getRowHeight: () => 'auto',
      }}
    />
  )
}

export default AnalysisChainsPage
